import React, { useMemo, useEffect, useState } from 'react';
import { ConfigProvider, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';

const AntSpin = Spin as React.ComponentType<any>;
const AntConfigProvider = ConfigProvider as React.ComponentType<any>;
import { useAndStore } from '@nuwatop/code-canvas-core';
import { ComponentRegistry, getComponent } from './ComponentRegistry';
import { parsePageConfig, type PageConfig, type ComponentConfig } from './configParser';
import { ActionExecutor } from './ActionExecutor';

export interface RuntimeRendererProps {
  /** 页面配置 JSON */
  pageConfig: PageConfig | string;
  /** 会话 ID */
  sessionId?: string;
  /** 是否开发模式 */
  develop?: boolean;
  /** 自定义主题 */
  theme?: any;
  /** 加载状态 */
  loading?: boolean;
  /** 错误处理 */
  onError?: (error: Error) => void;
}

/**
 * RuntimeRenderer - 渲染引擎组件
 * 将 GrapesJS 生成的页面配置渲染为 React 组件树
 */
export const RuntimeRenderer: React.FC<RuntimeRendererProps> = ({
  pageConfig,
  sessionId = `runtime_${Date.now()}`,
  develop = false,
  theme,
  loading = false,
  onError,
}) => {
  const [parsedConfig, setParsedConfig] = useState<PageConfig | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // 解析配置
  useEffect(() => {
    try {
      const config = typeof pageConfig === 'string' 
        ? parsePageConfig(pageConfig) 
        : pageConfig;
      setParsedConfig(config);
      setError(null);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      onError?.(e);
    }
  }, [pageConfig, onError]);

  // 渲染单个组件
  const renderComponent = (config: ComponentConfig, index: number): React.ReactNode => {
    const Component = getComponent(config.type);
    
    if (!Component) {
      if (develop) {
        return (
          <div key={config.id || index} style={{ padding: 8, background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 4 }}>
            未知组件: {config.type}
          </div>
        );
      }
      console.warn(`Unknown component type: ${config.type}`);
      return null;
    }

    const props = {
      ...config.props,
      id: config.id,
      sessionId,
      develop,
      key: config.id || index,
    };

    // 递归渲染子组件
    const children = config.children?.map((child, idx) => renderComponent(child, idx));

    return React.createElement(Component, props, children);
  };

  // 渲染页面
  const content = useMemo(() => {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <AntSpin size="large" />
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ padding: 24, background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8 }}>
          <h3 style={{ color: '#cf1322' }}>渲染错误</h3>
          <p>{error.message}</p>
        </div>
      );
    }

    if (!parsedConfig) {
      return null;
    }

    return (
      <div className="runtime-container" style={parsedConfig.style}>
        {parsedConfig.components.map((comp, idx) => renderComponent(comp, idx))}
      </div>
    );
  }, [loading, error, parsedConfig, sessionId, develop]);

  return (
    <AntConfigProvider locale={zhCN} theme={theme}>
      {content}
    </AntConfigProvider>
  );
};

export default RuntimeRenderer;
